import { Fragment, useEffect, useMemo, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import armorZone from '~/assets/icons/armor_zone.svg';
import cards from '~/assets/icons/cards_2.svg';
import { ImgIcon } from '~/components/ui/ImgIcon';
import { Text } from '~/components/ui/Text';
import { useZoomFactor } from '~/hooks';
import { rage, useObservable } from '~/rage';
import { WalletType } from '~/shared';
import { IconStroke } from '../IconStroke';
import { Weather } from '../Weather';
import {
	StyledBankBalanceContainer,
	StyledContainer,
	StyledDateTimeContainer,
	StyledGameAreaContainer,
	StyledGreenZoneContainer,
	StyledMoneyDeposit,
	StyledTimeContainer,
} from './MapInfo.styled';

const MapInfo: React.FC = () => {
	const realTime = useObservable(rage.game.getRealTime());
	const gameArea = useObservable(rage.game.getArea());
	const bankMoneyAmount = useObservable(rage.bank.getBalance(WalletType.Bank));
	const moneyDiff = useObservable(rage.bank.getBalanceDiff(WalletType.Bank));
	const minimapInfo = useObservable(rage.gui.getMinimapInfo());
	const isInGreenZone = useObservable(rage.game.getIsInGreenZone());

	const [moneyVisible, setMoneyVisible] = useState(false);

	const accountId = useObservable(rage.player.getAccountId());
	const serverName = useObservable(rage.game.getServerName());
	const systemString = useMemo(() => {
		return 'UID: #' + (accountId ?? '0') + ' | ' + serverName;
	}, [accountId, serverName]);

	useEffect(() => {
		if (moneyDiff !== 0) {
			setMoneyVisible(true);

			const timer = setTimeout(() => {
				setMoneyVisible(false);
			}, 5000);

			return () => clearTimeout(timer);
		}
	}, [moneyDiff]);

	useEffect(() => {
		return () => {
			rage.bank.finishDisplayingBalanceDiff(WalletType.Bank);
		};
	}, []);

	const zoomFactor = useZoomFactor();
	const minimapOffsetX = useMemo(() => 20 * zoomFactor, [zoomFactor]);

	const greenZoneTopInsetPx = 10;
	const greenZoneLeftInsetPx = 10;
	const greenZonePlaqueHeightPx = 20;
	const infoContainerX = minimapInfo.x + minimapInfo.width + minimapOffsetX;
	const infoContainerBottomY = minimapInfo.y;
	const greenZoneLeftX = minimapInfo.x + greenZoneLeftInsetPx * zoomFactor;
	const greenZoneBottomY =
		minimapInfo.y +
		minimapInfo.height -
		greenZoneTopInsetPx -
		greenZonePlaqueHeightPx;

	return (
		<Fragment>
			<CSSTransition
				in={isInGreenZone}
				timeout={300}
				classNames="green-zone-fade"
				unmountOnExit
			>
				<StyledGreenZoneContainer
					$x={greenZoneLeftX}
					$bottom={greenZoneBottomY}
				>
					<ImgIcon icon={armorZone} width={12} height={12} color="#77DE38" />
					<Text>БЕЗОПАСНО</Text>
				</StyledGreenZoneContainer>
			</CSSTransition>

			<StyledContainer $x={infoContainerX} $y={infoContainerBottomY}>
				<StyledDateTimeContainer>
					<StyledTimeContainer>
						<Text style={{ fontWeight: 700, fontSize: '18px', width: 48 }}>
							{realTime.time}
						</Text>
						<Weather />
					</StyledTimeContainer>
					<Text style={{ fontSize: '16px' }}>
						<span style={{ fontWeight: 600 }}>{realTime.weekday}</span>
						<span style={{ fontWeight: 400 }}>, </span>
						<span style={{ fontWeight: 500, color: '#e0e0e0' }}>
							{realTime.date}
						</span>
					</Text>
				</StyledDateTimeContainer>
				<StyledBankBalanceContainer>
					<IconStroke src={cards} width={16} height={16} />
					<Text style={{ fontWeight: 700, fontSize: '18px' }}>
						{bankMoneyAmount.toLocaleString('ru-RU')}
					</Text>
					<CSSTransition
						in={moneyVisible}
						classNames="fade"
						timeout={500}
						unmountOnExit
						onExited={() =>
							rage.bank.finishDisplayingBalanceDiff(WalletType.Bank)
						}
					>
						<StyledMoneyDeposit $negative={moneyDiff < 0}>
							{moneyDiff > 0
								? `+ ${moneyDiff.toLocaleString('ru-RU')}`
								: `- ${Math.abs(moneyDiff).toLocaleString('ru-RU')}`}
						</StyledMoneyDeposit>
					</CSSTransition>
				</StyledBankBalanceContainer>
				<StyledGameAreaContainer>
					<Text style={{ fontWeight: 700, color: '#FABE2C' }}>
						{gameArea.primaryName}
					</Text>
					<Text style={{ color: '#ffffff', fontWeight: 500 }}>
						{gameArea.secondaryName}
					</Text>
				</StyledGameAreaContainer>
				<Text
					style={{
						color: '#e0e0e0',
						fontSize: '14px',
						marginTop: 6,
						fontWeight: 500,
					}}
				>
					{systemString}
				</Text>
			</StyledContainer>
		</Fragment>
	);
};

export { MapInfo };
